/**
 * VideoRoom.jsx — In-house WebRTC video conferencing
 * ─────────────────────────────────────────────────
 * • Signaling : Cloud Firestore (no third-party video API / payments)
 * • Transport : RTCPeerConnection — browser-native WebRTC
 * • Recording : MediaRecorder API → Firebase Storage
 * • Audio mix : WebAudio API merges local + remote before recording
 * • Screen share : getDisplayMedia replaces video track on-the-fly
 * • Transcript  : Web Speech API (chrome / chromium)
 * • Tasks       : create Firestore tasks during the meeting
 *
 * Firestore structure:
 *   video_rooms/{roomId}
 *     offer            SDP offer  { type, sdp }
 *     answer           SDP answer { type, sdp }
 *     status           'waiting' | 'connected' | 'ended'
 *     recordings[]     [{ url, recordedAt }]
 *     callerCandidates/{id}  ICE candidate JSON
 *     calleeCandidates/{id}  ICE candidate JSON
 *   meeting_transcripts/{roomId}
 *     transcript[]     [{ text, speaker, time }]
 *     minutes          plain-text export
 *     savedAt          serverTimestamp
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { getApp } from 'firebase/app';
import {
  getFirestore, doc, collection, addDoc, getDoc, setDoc,
  updateDoc, onSnapshot, serverTimestamp, arrayUnion,
} from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { actorStamp } from '../utils/actorStamp';

// ── Lazy Firebase instances ───────────────────────────────────────────────────
let _db = null, _stg = null;
const fdb  = () => { if (!_db)  { try { _db  = getFirestore(getApp()); } catch (_) {} } return _db;  };
const fstg = () => { if (!_stg) { try { _stg = getStorage(getApp());   } catch (_) {} } return _stg; };

// ── ICE / STUN configuration (all free, no API key) ──────────────────────────
const ICE_CONFIG = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302'  },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    { urls: 'stun:stun3.l.google.com:19302' },
  ],
};

// ── Best recording MIME type supported by this browser ────────────────────────
function getBestMime() {
  for (const t of [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4',
  ]) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)) return t;
  }
  return '';
}

// ─────────────────────────────────────────────────────────────────────────────
//  useWebRTC — manages the full WebRTC peer-connection lifecycle
// ─────────────────────────────────────────────────────────────────────────────
function useWebRTC({ roomId, onEnd }) {
  // Refs for DOM elements
  const localRef  = useRef(null);   // <video> for local camera preview
  const remoteRef = useRef(null);   // <video> for remote stream

  // Refs for internal state (not re-render triggers)
  const pcRef            = useRef(null);               // RTCPeerConnection
  const localStreamRef   = useRef(null);               // local getUserMedia stream
  const remoteStreamRef  = useRef(new MediaStream());  // accumulates remote tracks
  const pendingCandidates = useRef([]);                // ICE candidates queued before setRemoteDesc
  const unsubsRef        = useRef([]);                 // Firestore listener unsub fns
  const recorderRef      = useRef(null);               // MediaRecorder
  const chunksRef        = useRef([]);                 // recording data chunks
  const audioCtxRef      = useRef(null);               // WebAudio mixing context

  // React state
  const [status,        setStatus]        = useState('init');      // init|waiting|connecting|connected|ended|error
  const [role,          setRole]          = useState('');          // 'caller' | 'callee'
  const [audioMuted,    setAudioMuted]    = useState(false);
  const [videoMuted,    setVideoMuted]    = useState(false);
  const [recording,     setRecording]     = useState(false);
  const [uploading,     setUploading]     = useState(false);
  const [recUrl,        setRecUrl]        = useState('');
  const [error,         setError]         = useState('');
  const [screenSharing, setScreenSharing] = useState(false);

  // ── Flush any ICE candidates queued before remote description was set ────────
  async function flushPending(pc) {
    while (pendingCandidates.current.length) {
      const c = pendingCandidates.current.shift();
      await pc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {});
    }
  }

  // ── Safe ICE candidate addition ──────────────────────────────────────────────
  async function safeAddIce(pc, data) {
    if (!pc.remoteDescription) {
      pendingCandidates.current.push(data);
    } else {
      await pc.addIceCandidate(new RTCIceCandidate(data)).catch(() => {});
    }
  }

  // ── Release all resources ────────────────────────────────────────────────────
  const cleanup = useCallback(() => {
    localStreamRef.current?.getTracks().forEach(t => t.stop());
    pcRef.current?.close();
    unsubsRef.current.forEach(u => u());
    unsubsRef.current = [];
    audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;
  }, []);

  // ── CALLER flow ──────────────────────────────────────────────────────────────
  async function startAsCaller(pc, roomRef) {
    setRole('caller');
    setStatus('waiting');

    // Save ICE candidates to Firestore as they arrive
    pc.onicecandidate = async e => {
      if (!e.candidate) return;
      await addDoc(collection(roomRef, 'callerCandidates'), e.candidate.toJSON()).catch(() => {});
    };

    // Create and persist the SDP offer
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await setDoc(roomRef, {
      offer:     { type: offer.type, sdp: offer.sdp },
      status:    'waiting',
      createdAt: serverTimestamp(),
    });

    // Listen for callee answer
    const unsubRoom = onSnapshot(roomRef, async snap => {
      const data = snap.data() || {};
      if (data.answer && !pc.currentRemoteDescription) {
        await pc.setRemoteDescription(new RTCSessionDescription(data.answer)).catch(() => {});
        await flushPending(pc);
        setStatus('connecting');
      }
      if (data.status === 'ended') { setStatus('ended'); onEnd?.(); }
    });
    unsubsRef.current.push(unsubRoom);

    // Listen for callee ICE candidates
    const unsubIce = onSnapshot(collection(roomRef, 'calleeCandidates'), snap => {
      snap.docChanges().forEach(async ch => {
        if (ch.type === 'added') await safeAddIce(pc, ch.doc.data());
      });
    });
    unsubsRef.current.push(unsubIce);
  }

  // ── CALLEE flow ──────────────────────────────────────────────────────────────
  async function startAsCallee(pc, roomRef, offerData) {
    setRole('callee');
    setStatus('connecting');

    // Save ICE candidates to Firestore as they arrive
    pc.onicecandidate = async e => {
      if (!e.candidate) return;
      await addDoc(collection(roomRef, 'calleeCandidates'), e.candidate.toJSON()).catch(() => {});
    };

    // Set remote offer, create and send answer
    await pc.setRemoteDescription(new RTCSessionDescription(offerData));
    await flushPending(pc);

    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    await updateDoc(roomRef, {
      answer: { type: answer.type, sdp: answer.sdp },
      status: 'connected',
    });

    // Listen for room end signal
    const unsubRoom = onSnapshot(roomRef, snap => {
      if (snap.data()?.status === 'ended') { setStatus('ended'); onEnd?.(); }
    });
    unsubsRef.current.push(unsubRoom);

    // Listen for caller ICE candidates
    const unsubIce = onSnapshot(collection(roomRef, 'callerCandidates'), snap => {
      snap.docChanges().forEach(async ch => {
        if (ch.type === 'added') await safeAddIce(pc, ch.doc.data());
      });
    });
    unsubsRef.current.push(unsubIce);
  }

  // ── Full initialization ──────────────────────────────────────────────────────
  const init = useCallback(async () => {
    setError('');
    setStatus('init');
    try {
      // 1. Capture local camera + microphone
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      localStreamRef.current = stream;
      if (localRef.current) {
        localRef.current.srcObject = stream;
      }

      // 2. Create the RTCPeerConnection
      const pc = new RTCPeerConnection(ICE_CONFIG);
      pcRef.current = pc;

      // 3. Add all local tracks to the connection
      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      // 4. Wire up incoming remote tracks → remoteStream → <video>
      pc.ontrack = event => {
        event.streams[0].getTracks().forEach(track => {
          // Avoid duplicate tracks on renegotiation
          const existing = remoteStreamRef.current.getTracks().find(t => t.id === track.id);
          if (!existing) remoteStreamRef.current.addTrack(track);
        });
        if (remoteRef.current) remoteRef.current.srcObject = remoteStreamRef.current;
      };

      // 5. Track connection state changes
      pc.onconnectionstatechange = () => {
        const s = pc.connectionState;
        if (s === 'connected')                       setStatus('connected');
        if (s === 'disconnected' || s === 'failed')  setStatus('connecting'); // may recover
        if (s === 'closed')                          setStatus('ended');
      };

      // 6. Auto-detect role: first to arrive creates the offer (caller)
      const roomRef  = doc(fdb(), 'video_rooms', roomId);
      const roomSnap = await getDoc(roomRef);

      if (!roomSnap.exists() || !roomSnap.data()?.offer) {
        await startAsCaller(pc, roomRef);
      } else {
        await startAsCallee(pc, roomRef, roomSnap.data().offer);
      }
    } catch (err) {
      setError(err.message || 'Could not access camera / microphone');
      setStatus('error');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  // Mount: start WebRTC. Unmount: release everything.
  useEffect(() => {
    init();
    return cleanup;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Audio / Video toggles ────────────────────────────────────────────────────
  function toggleAudio() {
    localStreamRef.current?.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
    setAudioMuted(m => !m);
  }

  function toggleVideo() {
    localStreamRef.current?.getVideoTracks().forEach(t => { t.enabled = !t.enabled; });
    setVideoMuted(m => !m);
  }

  // ── Screen sharing ───────────────────────────────────────────────────────────
  async function toggleScreenShare() {
    try {
      if (screenSharing) {
        // Revert to camera
        const camStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        const videoTrack = camStream.getVideoTracks()[0];
        const sender = pcRef.current?.getSenders().find(s => s.track?.kind === 'video');
        if (sender && videoTrack) await sender.replaceTrack(videoTrack);
        if (localRef.current) localRef.current.srcObject = camStream;
        // Stop old tracks
        localStreamRef.current?.getTracks().forEach(t => t.stop());
        localStreamRef.current = camStream;
        setScreenSharing(false);
      } else {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
        const screenTrack = screenStream.getVideoTracks()[0];
        const sender = pcRef.current?.getSenders().find(s => s.track?.kind === 'video');
        if (sender && screenTrack) await sender.replaceTrack(screenTrack);
        if (localRef.current) localRef.current.srcObject = screenStream;
        screenTrack.onended = () => toggleScreenShare(); // user stopped via browser UI
        setScreenSharing(true);
      }
    } catch (err) {
      console.warn('Screen share error:', err);
    }
  }

  // ── Recording ────────────────────────────────────────────────────────────────
  function startRecording() {
    const mimeType = getBestMime();
    chunksRef.current = [];

    try {
      // Mix local mic + remote audio into one stream using WebAudio API
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = audioCtx;
      const dest = audioCtx.createMediaStreamDestination();

      if (localStreamRef.current?.getAudioTracks().length) {
        audioCtx.createMediaStreamSource(localStreamRef.current).connect(dest);
      }
      if (remoteStreamRef.current?.getAudioTracks().length) {
        audioCtx.createMediaStreamSource(remoteStreamRef.current).connect(dest);
      }

      // Combine local video + mixed audio into the recording stream
      const recStream = new MediaStream([
        ...(localStreamRef.current?.getVideoTracks() || []),
        ...dest.stream.getAudioTracks(),
      ]);

      const recorder = new MediaRecorder(recStream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;

      recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop          = () => uploadRecording(mimeType);
      recorder.start(1000); // collect a chunk every second
      setRecording(true);
    } catch (err) {
      console.error('Recording start failed:', err);
    }
  }

  function stopRecording() {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    setRecording(false);
  }

  async function uploadRecording(mimeType) {
    if (!chunksRef.current.length) return;
    setUploading(true);
    try {
      const ext  = mimeType?.includes('mp4') ? 'mp4' : 'webm';
      const blob = new Blob(chunksRef.current, { type: mimeType || 'video/webm' });
      const path = `recordings/video_${roomId}_${Date.now()}.${ext}`;
      const ref  = storageRef(fstg(), path);
      await uploadBytes(ref, blob);
      const url  = await getDownloadURL(ref);
      setRecUrl(url);
      await updateDoc(doc(fdb(), 'video_rooms', roomId), {
        recordings: arrayUnion({ url, recordedAt: new Date().toISOString() }),
      }).catch(() => {});
    } catch (err) {
      console.error('Recording upload failed:', err);
    }
    setUploading(false);
    audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;
  }

  // ── Hang up ──────────────────────────────────────────────────────────────────
  const hangUp = useCallback(async () => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    try {
      await updateDoc(doc(fdb(), 'video_rooms', roomId), { status: 'ended' });
    } catch (_) {}
    cleanup();
    onEnd?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, cleanup]);

  return {
    localRef, remoteRef,
    status, role, error,
    audioMuted, videoMuted,
    recording, uploading, recUrl,
    toggleAudio, toggleVideo,
    startRecording, stopRecording,
    hangUp,
    screenSharing, toggleScreenShare,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
//  VideoRoom — full-screen video call UI
//
//  Props:
//    roomId           string   Firestore document ID used for signaling
//    user             object   { uid, name }
//    onLeave          fn       Called when user hangs up or remote ends call
//    recordingEnabled bool     Show recording controls
// ─────────────────────────────────────────────────────────────────────────────
export default function VideoRoom({ roomId, user, onLeave, recordingEnabled = false }) {
  const {
    localRef, remoteRef,
    status, role, error,
    audioMuted, videoMuted,
    recording, uploading, recUrl,
    toggleAudio, toggleVideo,
    startRecording, stopRecording,
    hangUp,
    screenSharing, toggleScreenShare,
  } = useWebRTC({ roomId, onEnd: onLeave });

  const [copied, setCopied] = useState(false);

  // ── Transcript state ─────────────────────────────────────────────────────────
  const [transcript,   setTranscript]   = useState([]);  // [{text, speaker, time}]
  const [transcribing, setTranscribing] = useState(false);
  const [showPanel,    setShowPanel]    = useState('none'); // 'none'|'transcript'|'tasks'
  const recognitionRef = useRef(null);

  // ── Task state ───────────────────────────────────────────────────────────────
  const [meetingTasks,  setMeetingTasks]  = useState([]);
  const [taskTitle,     setTaskTitle]     = useState('');
  const [taskAssignee,  setTaskAssignee]  = useState('');
  const [savingTask,    setSavingTask]    = useState(false);
  const [members,       setMembers]       = useState([]);

  // ── Load team members when tasks panel opens ─────────────────────────────────
  useEffect(() => {
    if (showPanel !== 'tasks') return;
    const unsub = onSnapshot(collection(fdb(), 'authorized_users'), snap => {
      setMembers(snap.docs.map(d => ({ uid: d.id, ...d.data() })).filter(m => m.active !== false));
    }, () => {});
    return unsub;
  }, [showPanel]);

  function copyRoomId() {
    navigator.clipboard.writeText(roomId).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }

  // ── Transcript toggle ────────────────────────────────────────────────────────
  function toggleTranscript() {
    if (transcribing) {
      recognitionRef.current?.stop();
      setTranscribing(false);
    } else {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SR) { alert('Speech recognition not supported in this browser. Use Chrome.'); return; }
      const rec = new SR();
      rec.continuous = true;
      rec.interimResults = false;
      rec.lang = 'en-IN';
      rec.onresult = e => {
        const text = e.results[e.results.length - 1][0].transcript.trim();
        if (text) {
          const entry = { text, speaker: user.name, time: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) };
          setTranscript(prev => [...prev, entry]);
        }
      };
      rec.onerror = () => setTranscribing(false);
      rec.onend = () => { if (transcribing) rec.start(); }; // keep running
      rec.start();
      recognitionRef.current = rec;
      setTranscribing(true);
    }
  }

  // ── Save transcript to Firestore ─────────────────────────────────────────────
  async function saveTranscript() {
    if (!transcript.length || !roomId) return;
    try {
      const minutes = transcript.map(e => `[${e.time}] ${e.speaker}: ${e.text}`).join('\n');
      await setDoc(doc(fdb(), 'meeting_transcripts', roomId), {
        roomId,
        transcript,
        minutes,
        savedAt: serverTimestamp(),
        savedBy: user.name,
        savedByUid: user.uid,
      }, { merge: true });
      alert('Transcript saved as meeting minutes!');
    } catch (err) {
      alert('Save failed: ' + err.message);
    }
  }

  // ── Create task from meeting ─────────────────────────────────────────────────
  async function createMeetingTask(e) {
    e.preventDefault();
    if (!taskTitle.trim()) return;
    setSavingTask(true);
    try {
      await addDoc(collection(fdb(), 'tasks'), { ...actorStamp(),
        title: taskTitle.trim(),
        description: `Set during meeting: ${roomId}`,
        status: 'pending',
        priority: 'medium',
        assigneeId: taskAssignee || null,
        assigneeName: members.find(m => m.uid === taskAssignee)?.name || '',
        createdBy: user.name,
        createdByUid: user.uid,
        portal: 'all',
        requiresProof: true, sourceType: 'manual',
        source: 'meeting',
        meetingRoom: roomId,
        createdAt: serverTimestamp(),
      });
      setMeetingTasks(prev => [...prev, { title: taskTitle.trim(), assignee: taskAssignee }]);
      setTaskTitle('');
      setTaskAssignee('');
    } catch (err) { console.warn(err); }
    setSavingTask(false);
  }

  const connected  = status === 'connected';
  const waiting    = status === 'waiting';
  const connecting = status === 'connecting';

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 500,
      background: '#000', display: 'flex', flexDirection: 'column',
      fontFamily: "'Inter',system-ui,sans-serif",
    }}>
      <style>{`
        @keyframes vr-pulse { 0%,100%{opacity:1} 50%{opacity:0.35} }
        @keyframes vr-fade  { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      {/* ── Remote stream — fills the screen ── */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden', background: '#080b12',
        marginRight: showPanel !== 'none' ? 320 : 0, transition: 'margin-right 0.2s' }}>

        <video ref={remoteRef} autoPlay playsInline
          style={{
            width: '100%', height: '100%', objectFit: 'cover',
            display: connected ? 'block' : 'none',
          }}
        />

        {/* Waiting / connecting overlay */}
        {!connected && !error && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 20,
            background: 'radial-gradient(ellipse at center, #0c0f18 0%, #000 100%)',
          }}>
            <div style={{ fontSize: 64, animation: 'vr-pulse 2s infinite' }}>
              {waiting ? '⏳' : '🔗'}
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 17, fontWeight: 700, color: '#6366f1' }}>
              {waiting    ? 'Waiting for others to join…' : ''}
              {connecting ? 'Connecting…' : ''}
              {status === 'init' ? 'Starting camera…' : ''}
            </div>

            {/* Room ID share card — shown to caller while waiting */}
            {waiting && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: '#0c0f18', border: '1px solid #1e2636',
                borderRadius: 10, padding: '12px 18px',
                animation: 'vr-fade 0.4s ease',
              }}>
                <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#6e7fa8' }}>
                  Share Room ID:
                </span>
                <code style={{
                  fontFamily: 'monospace', fontSize: 13, color: '#c5d8e8',
                  letterSpacing: 1, userSelect: 'all',
                }}>
                  {roomId}
                </code>
                <button onClick={copyRoomId} style={{
                  background: 'transparent',
                  border: `1px solid ${copied ? '#10b98155' : '#6366f133'}`,
                  color: copied ? '#10b981' : '#6366f1',
                  fontFamily: 'monospace', fontSize: 9, padding: '5px 12px',
                  cursor: 'pointer', borderRadius: 4, letterSpacing: 1, transition: 'all 0.2s',
                }}>
                  {copied ? '✓ COPIED' : 'COPY'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Error overlay */}
        {error && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 18, padding: 40,
          }}>
            <div style={{ fontSize: 52 }}>⚠️</div>
            <div style={{
              fontFamily: 'monospace', fontSize: 13, color: '#f43f5e',
              textAlign: 'center', maxWidth: 420, lineHeight: 1.6,
            }}>
              {error}
            </div>
            <button onClick={onLeave} style={{
              padding: '10px 28px', background: '#f43f5e22',
              border: '1px solid #f43f5e', color: '#f43f5e',
              fontFamily: 'monospace', fontSize: 11, cursor: 'pointer',
              borderRadius: 6, letterSpacing: 1,
            }}>
              LEAVE ROOM
            </button>
          </div>
        )}

        {/* ── Status badges (top-left) ── */}
        <div style={{ position: 'absolute', top: 16, left: 16, display: 'flex', gap: 8 }}>
          {connected && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 7,
              background: '#00000099', backdropFilter: 'blur(8px)',
              borderRadius: 20, padding: '5px 14px', border: '1px solid #10b98133',
            }}>
              <div style={{
                width: 8, height: 8, borderRadius: '50%',
                background: '#10b981', animation: 'vr-pulse 2s infinite',
              }} />
              <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#10b981', letterSpacing: 1 }}>
                LIVE
              </span>
            </div>
          )}
          {recording && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 7,
              background: '#00000099', backdropFilter: 'blur(8px)',
              borderRadius: 20, padding: '5px 14px', border: '1px solid #f43f5e44',
              animation: 'vr-pulse 1.5s infinite',
            }}>
              <div style={{
                width: 8, height: 8, borderRadius: '50%',
                background: '#f43f5e',
              }} />
              <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#f43f5e', letterSpacing: 1 }}>
                REC
              </span>
            </div>
          )}
        </div>

        {/* ── Room ID + copy button (top-right) ── */}
        <div style={{
          position: 'absolute', top: 16, right: 16,
          display: 'flex', alignItems: 'center', gap: 8,
          background: '#00000099', backdropFilter: 'blur(8px)',
          borderRadius: 8, padding: '6px 12px',
        }}>
          <span style={{ fontFamily: 'monospace', fontSize: 9, color: '#6e7fa8', letterSpacing: 1 }}>
            ROOM
          </span>
          <code style={{ fontFamily: 'monospace', fontSize: 11, color: '#c5d8e8' }}>
            {roomId.slice(-10)}
          </code>
          <button onClick={copyRoomId} style={{
            background: 'transparent', border: 'none',
            color: copied ? '#10b981' : '#6e7fa8',
            fontFamily: 'monospace', fontSize: 9,
            cursor: 'pointer', letterSpacing: 1, padding: '0 4px',
          }}>
            {copied ? '✓' : 'COPY'}
          </button>
        </div>

        {/* ── Local camera — picture-in-picture (bottom-right) ── */}
        <div style={{
          position: 'absolute', bottom: 16, right: 16,
          width: 190, height: 126, borderRadius: 12, overflow: 'hidden',
          border: '2px solid #1e2636', boxShadow: '0 4px 24px #000000bb',
        }}>
          <video ref={localRef} autoPlay playsInline muted
            style={{
              width: '100%', height: '100%', objectFit: 'cover',
              background: '#0c0f18',
              transform: screenSharing ? 'none' : 'scaleX(-1)', // no mirror for screen share
              display: videoMuted ? 'none' : 'block',
            }}
          />
          {videoMuted && (
            <div style={{
              width: '100%', height: '100%', background: '#0c0f18',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32,
            }}>
              🚫
            </div>
          )}
          <div style={{
            position: 'absolute', bottom: 5, left: 8,
            fontFamily: 'monospace', fontSize: 9, color: '#6e7fa8aa',
          }}>
            {screenSharing ? 'SCREEN' : 'YOU'}
          </div>
          {audioMuted && (
            <div style={{
              position: 'absolute', top: 5, right: 6,
              fontFamily: 'monospace', fontSize: 14,
            }}>🔇</div>
          )}
        </div>

        {/* ── Upload / saved recording toast (above controls) ── */}
        {(uploading || recUrl) && (
          <div style={{
            position: 'absolute', bottom: 80, left: '50%',
            transform: 'translateX(-50%)',
            animation: 'vr-fade 0.3s ease',
            background: '#0c0f18',
            border: `1px solid ${uploading ? '#f59e0b' : '#10b981'}`,
            borderRadius: 8, padding: '10px 22px',
            fontFamily: 'monospace', fontSize: 11,
            color: uploading ? '#f59e0b' : '#10b981',
            whiteSpace: 'nowrap',
          }}>
            {uploading
              ? '☁ Uploading recording…'
              : <>✓ Recording saved — <a href={recUrl} target="_blank" rel="noreferrer"
                  style={{ color: '#10b981', textDecoration: 'underline' }}>View</a></>
            }
          </div>
        )}
      </div>

      {/* ── Side panel (transcript / tasks) ── */}
      {showPanel !== 'none' && (
        <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 320, zIndex: 90,
          background: '#0c0f18', borderLeft: '1px solid #1e2636',
          display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>

          {/* Panel tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid #1e2636' }}>
            <button onClick={() => setShowPanel('transcript')}
              style={{ flex: 1, padding: '12px 0', fontFamily: 'monospace', fontSize: 9, letterSpacing: 1,
                background: 'transparent', border: 'none', cursor: 'pointer',
                borderBottom: `2px solid ${showPanel === 'transcript' ? '#6366f1' : 'transparent'}`,
                color: showPanel === 'transcript' ? '#6366f1' : '#6e7fa8' }}>
              📝 TRANSCRIPT
            </button>
            <button onClick={() => setShowPanel('tasks')}
              style={{ flex: 1, padding: '12px 0', fontFamily: 'monospace', fontSize: 9, letterSpacing: 1,
                background: 'transparent', border: 'none', cursor: 'pointer',
                borderBottom: `2px solid ${showPanel === 'tasks' ? '#f59e0b' : 'transparent'}`,
                color: showPanel === 'tasks' ? '#f59e0b' : '#6e7fa8' }}>
              📋 TASKS
            </button>
            <button onClick={() => setShowPanel('none')}
              style={{ background: 'none', border: 'none', color: '#6e7fa8', cursor: 'pointer', padding: '0 14px', fontSize: 18 }}>×</button>
          </div>

          {/* Transcript panel */}
          {showPanel === 'transcript' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid #1e2636', display: 'flex', gap: 8 }}>
                <button onClick={toggleTranscript}
                  style={{ flex: 1, padding: '7px 0', fontFamily: 'monospace', fontSize: 9, letterSpacing: 1,
                    background: transcribing ? '#ef444418' : '#6366f118',
                    border: `1px solid ${transcribing ? '#ef4444' : '#6366f1'}`,
                    color: transcribing ? '#ef4444' : '#6366f1', cursor: 'pointer', borderRadius: 6 }}>
                  {transcribing ? '⏹ STOP' : '🎙 START RECORDING'}
                </button>
                {transcript.length > 0 && (
                  <button onClick={saveTranscript}
                    style={{ padding: '7px 12px', fontFamily: 'monospace', fontSize: 9, letterSpacing: 1,
                      background: '#10b98118', border: '1px solid #10b981',
                      color: '#10b981', cursor: 'pointer', borderRadius: 6 }}>
                    💾 SAVE
                  </button>
                )}
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px' }}>
                {transcript.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: '#445566', fontFamily: 'monospace', fontSize: 10 }}>
                    {transcribing ? 'Listening… speak clearly.' : 'Start transcript to capture voice.'}
                  </div>
                ) : transcript.map((entry, i) => (
                  <div key={i} style={{ marginBottom: 10, padding: '6px 10px',
                    background: '#111827', borderRadius: 6, borderLeft: '2px solid #6366f1' }}>
                    <div style={{ fontFamily: 'monospace', fontSize: 8, color: '#6366f1', marginBottom: 3 }}>
                      {entry.speaker} · {entry.time}
                    </div>
                    <div style={{ fontSize: 12, color: '#c5d8e8', lineHeight: 1.5 }}>{entry.text}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tasks panel */}
          {showPanel === 'tasks' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '12px 14px', borderBottom: '1px solid #1e2636' }}>
                <div style={{ fontFamily: 'monospace', fontSize: 9, color: '#f59e0b', letterSpacing: 2, marginBottom: 10 }}>
                  CREATE TASK FROM MEETING
                </div>
                <form onSubmit={createMeetingTask}>
                  <input value={taskTitle} onChange={e => setTaskTitle(e.target.value)}
                    placeholder="Task title *"
                    style={{ width: '100%', background: '#0b0e12', border: '1px solid #1e2636', color: '#c5d8e8',
                      padding: '8px 10px', fontFamily: 'monospace', fontSize: 11, borderRadius: 6,
                      outline: 'none', marginBottom: 8, boxSizing: 'border-box' }} />
                  <select value={taskAssignee} onChange={e => setTaskAssignee(e.target.value)}
                    style={{ width: '100%', background: '#0b0e12', border: '1px solid #1e2636', color: '#c5d8e8',
                      padding: '8px 10px', fontFamily: 'monospace', fontSize: 11, borderRadius: 6,
                      marginBottom: 10, boxSizing: 'border-box' }}>
                    <option value="">Assign to… (optional)</option>
                    {members.map(m => (
                      <option key={m.uid} value={m.uid}>{m.name || m.email}</option>
                    ))}
                  </select>
                  <button type="submit" disabled={savingTask || !taskTitle.trim()}
                    style={{ width: '100%', padding: '8px 0', fontFamily: 'monospace', fontSize: 9, letterSpacing: 1,
                      background: '#f59e0b18', border: '1px solid #f59e0b',
                      color: '#f59e0b', cursor: savingTask ? 'wait' : 'pointer', borderRadius: 6 }}>
                    {savingTask ? 'CREATING…' : '+ CREATE TASK'}
                  </button>
                </form>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px' }}>
                {meetingTasks.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: '#445566', fontFamily: 'monospace', fontSize: 10 }}>
                    No tasks created yet in this meeting.
                  </div>
                ) : meetingTasks.map((t, i) => (
                  <div key={i} style={{ padding: '8px 10px', marginBottom: 6,
                    background: '#111827', borderRadius: 6, borderLeft: '2px solid #f59e0b' }}>
                    <div style={{ fontSize: 12, color: '#c5d8e8', fontWeight: 600 }}>{t.title}</div>
                    {t.assignee && members.find(m => m.uid === t.assignee) && (
                      <div style={{ fontFamily: 'monospace', fontSize: 9, color: '#6e7fa8', marginTop: 2 }}>
                        → {members.find(m => m.uid === t.assignee)?.name}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Controls bar (bottom) ── */}
      <div style={{
        background: '#0c0f18', borderTop: '1px solid #1e2636',
        padding: '14px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 14, flexWrap: 'wrap',
      }}>

        {/* Microphone toggle */}
        <button
          onClick={toggleAudio}
          title={audioMuted ? 'Unmute' : 'Mute microphone'}
          style={{
            width: 52, height: 52, borderRadius: '50%',
            border: `2px solid ${audioMuted ? '#f43f5e' : '#2d3748'}`,
            background: audioMuted ? '#f43f5e22' : '#1e2636',
            cursor: 'pointer', fontSize: 22, transition: 'all 0.18s',
          }}
        >
          {audioMuted ? '🔇' : '🎤'}
        </button>

        {/* Camera toggle */}
        <button
          onClick={toggleVideo}
          title={videoMuted ? 'Enable camera' : 'Disable camera'}
          style={{
            width: 52, height: 52, borderRadius: '50%',
            border: `2px solid ${videoMuted ? '#f43f5e' : '#2d3748'}`,
            background: videoMuted ? '#f43f5e22' : '#1e2636',
            cursor: 'pointer', fontSize: 22, transition: 'all 0.18s',
          }}
        >
          {videoMuted ? '📵' : '📷'}
        </button>

        {/* Screen share */}
        <button onClick={toggleScreenShare}
          title={screenSharing ? 'Stop sharing' : 'Share screen'}
          style={{ padding: '10px 16px', borderRadius: 8, fontFamily: 'monospace', fontSize: 10, cursor: 'pointer',
            background: screenSharing ? '#10b98120' : 'transparent',
            border: `1px solid ${screenSharing ? '#10b981' : '#1e2636'}`,
            color: screenSharing ? '#10b981' : '#6e7fa8', letterSpacing: 1 }}>
          {screenSharing ? '🖥 SHARING' : '🖥 SHARE'}
        </button>

        {/* Recording controls */}
        {recordingEnabled && !recording && (
          <button onClick={startRecording} title="Start recording"
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '12px 20px', borderRadius: 26,
              border: '1px solid #2d3748', background: '#1e2636',
              color: '#c5d8e8', fontFamily: 'monospace', fontSize: 10,
              cursor: 'pointer', letterSpacing: 1,
            }}
          >
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f43f5e', flexShrink: 0 }} />
            RECORD
          </button>
        )}
        {recordingEnabled && recording && (
          <button onClick={stopRecording} title="Stop and save recording"
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '12px 20px', borderRadius: 26,
              border: '1px solid #f43f5e', background: '#f43f5e22',
              color: '#f43f5e', fontFamily: 'monospace', fontSize: 10,
              cursor: 'pointer', letterSpacing: 1,
              animation: 'vr-pulse 1.5s infinite',
            }}
          >
            <span style={{ width: 10, height: 10, borderRadius: 2, background: '#f43f5e', flexShrink: 0 }} />
            STOP REC
          </button>
        )}

        {/* Transcript toggle */}
        <button onClick={toggleTranscript}
          style={{ padding: '10px 14px', borderRadius: 8, fontFamily: 'monospace', fontSize: 10, cursor: 'pointer',
            background: transcribing ? '#ef444420' : 'transparent',
            border: `1px solid ${transcribing ? '#ef4444' : '#1e2636'}`,
            color: transcribing ? '#ef4444' : '#6e7fa8', letterSpacing: 1 }}>
          {transcribing ? '⏹ STOP TRANSCRIPT' : '🎙 TRANSCRIPT'}
        </button>

        {/* Open transcript panel */}
        <button onClick={() => setShowPanel(p => p === 'transcript' ? 'none' : 'transcript')}
          title="Voice transcript"
          style={{ padding: '10px 14px', borderRadius: 8, fontFamily: 'monospace', fontSize: 10, cursor: 'pointer',
            background: showPanel === 'transcript' ? '#6366f120' : 'transparent',
            border: `1px solid ${showPanel === 'transcript' ? '#6366f1' : '#1e2636'}`,
            color: showPanel === 'transcript' ? '#6366f1' : '#6e7fa8', letterSpacing: 1 }}>
          {transcribing ? '🔴 LIVE' : '📝 MINUTES'}
        </button>

        {/* Open tasks panel */}
        <button onClick={() => setShowPanel(p => p === 'tasks' ? 'none' : 'tasks')}
          title="Meeting tasks"
          style={{ padding: '10px 14px', borderRadius: 8, fontFamily: 'monospace', fontSize: 10, cursor: 'pointer',
            background: showPanel === 'tasks' ? '#f59e0b20' : 'transparent',
            border: `1px solid ${showPanel === 'tasks' ? '#f59e0b' : '#1e2636'}`,
            color: showPanel === 'tasks' ? '#f59e0b' : '#6e7fa8', letterSpacing: 1 }}>
          📋 TASKS {meetingTasks.length > 0 ? `(${meetingTasks.length})` : ''}
        </button>

        {/* Divider */}
        <div style={{ width: 1, height: 36, background: '#1e2636', flexShrink: 0 }} />

        {/* User badge */}
        <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#6e7fa8' }}>
          👤 {user?.name || 'You'}
          {role && (
            <span style={{
              marginLeft: 8,
              color: role === 'caller' ? '#6366f1' : '#10b981',
              fontSize: 9, letterSpacing: 1,
            }}>
              [{role.toUpperCase()}]
            </span>
          )}
        </div>

        {/* Hang up */}
        <button
          onClick={hangUp}
          title="Leave call"
          style={{
            width: 58, height: 58, borderRadius: '50%',
            border: 'none', background: '#f43f5e',
            cursor: 'pointer', fontSize: 24,
            boxShadow: '0 0 0 4px #f43f5e22',
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform='scale(1.12)'; e.currentTarget.style.boxShadow='0 0 0 8px #f43f5e33'; }}
          onMouseLeave={e => { e.currentTarget.style.transform='scale(1)';    e.currentTarget.style.boxShadow='0 0 0 4px #f43f5e22'; }}
        >
          📵
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  VideoRoomLobby — create a new room or join an existing one by ID
//  Used in the DIRECT CALL / MEETINGS tabs so team members can start
//  an instant video call without scheduling it first.
// ─────────────────────────────────────────────────────────────────────────────
export function VideoRoomLobby({ user, onJoin }) {
  const [roomInput, setRoomInput] = useState('');
  const [joining,   setJoining]   = useState(false);
  const [err,       setErr]       = useState('');

  function newRoomId() {
    return 'smc-' + Math.random().toString(36).slice(2, 9) + Date.now().toString(36).slice(-4);
  }

  function createRoom() {
    onJoin(newRoomId());
  }

  async function joinByRoomId() {
    const id = roomInput.trim();
    if (!id) { setErr('Please enter a Room ID'); return; }
    setErr('');
    setJoining(true);
    try {
      const snap = await getDoc(doc(fdb(), 'video_rooms', id));
      if (!snap.exists()) {
        setErr('Room not found — check the ID and try again.');
        setJoining(false);
        return;
      }
      if (snap.data()?.status === 'ended') {
        setErr('This room has already ended.');
        setJoining(false);
        return;
      }
    } catch (_) {
      // Firestore read failed (rules / network); let WebRTC handle it
    }
    setJoining(false);
    onJoin(id);
  }

  const inputStyle = {
    flex: 1, minWidth: 200,
    background: '#07080f', border: '1px solid #1e2636',
    color: '#c5d8e8', padding: '12px 16px',
    fontFamily: 'monospace', fontSize: 12, borderRadius: 8,
    outline: 'none',
  };

  return (
    <div style={{ padding: '28px 0' }}>
      <div style={{
        fontFamily: 'monospace', fontSize: 9, letterSpacing: 3, color: '#6366f1',
        borderBottom: '1px solid #6366f122', paddingBottom: 10, marginBottom: 24,
      }}>
        INSTANT VIDEO CALL — IN-HOUSE WEBRTC (NO THIRD-PARTY API)
      </div>

      {/* Create new room */}
      <button onClick={createRoom} style={{
        width: '100%', padding: '18px 24px', marginBottom: 14,
        background: '#6366f118', border: '1px solid #6366f155',
        color: '#6366f1', fontFamily: 'monospace', fontSize: 12,
        cursor: 'pointer', borderRadius: 10, letterSpacing: 1, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        transition: 'background 0.2s',
      }}
        onMouseEnter={e => e.currentTarget.style.background='#6366f128'}
        onMouseLeave={e => e.currentTarget.style.background='#6366f118'}
      >
        🎥 CREATE NEW VIDEO ROOM
      </button>

      {/* Join by ID */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          value={roomInput}
          onChange={e => { setRoomInput(e.target.value); setErr(''); }}
          onKeyDown={e => e.key === 'Enter' && joinByRoomId()}
          placeholder="Paste Room ID to join…"
          style={inputStyle}
        />
        <button onClick={joinByRoomId} disabled={joining} style={{
          padding: '12px 24px', background: '#1e2636',
          border: '1px solid #2d3748', color: '#c5d8e8',
          fontFamily: 'monospace', fontSize: 11,
          cursor: joining ? 'wait' : 'pointer', borderRadius: 8, letterSpacing: 1,
          whiteSpace: 'nowrap',
        }}>
          {joining ? 'Checking…' : 'JOIN ROOM →'}
        </button>
      </div>

      {err && (
        <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#f43f5e', marginTop: 10 }}>
          {err}
        </div>
      )}

      {/* Feature highlights */}
      <div style={{
        marginTop: 28, display: 'flex', flexDirection: 'column', gap: 6,
        fontFamily: 'monospace', fontSize: 10, color: '#6e7fa8', lineHeight: 1.9,
      }}>
        <div>✓ 100% in-house — Firestore signaling, browser WebRTC, no subscriptions</div>
        <div>✓ End-to-end peer-to-peer video &amp; audio (direct connection between browsers)</div>
        <div>✓ Recording saved to Firebase Storage (local + remote audio mixed via WebAudio API)</div>
        <div>✓ Auto-detects caller/callee — first to join creates the offer</div>
        <div>✓ Screen sharing — share your display without leaving the call</div>
        <div>✓ Live transcript — Web Speech API captures voice to meeting minutes</div>
        <div>✓ Task creation — create Firestore tasks from inside the meeting</div>
      </div>
    </div>
  );
}
